import React, { forwardRef } from 'react';
import { formatPaise } from '../lib/cn';
import { cn } from '../lib/cn';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from './Card';
import { Button } from './Button';

export interface CreditKhataCardProps extends React.HTMLAttributes<HTMLDivElement> {
  balance: number; // paise
  dueDate?: string;
  creditLimit?: number; // paise
  onPayNow?: () => void;
}

export const CreditKhataCard = forwardRef<HTMLDivElement, CreditKhataCardProps>(
  ({ balance, dueDate, creditLimit = 0, onPayNow, className, ...props }, ref) => {
    
    let utilization = 0;
    if (creditLimit > 0) {
      utilization = Math.min(100, Math.max(0, (balance / creditLimit) * 100));
    }
    
    let statusColorClass = "bg-success";
    let textColorClass = "text-success";
    
    if (utilization >= 80) {
      statusColorClass = "bg-destructive";
      textColorClass = "text-destructive";
    } else if (utilization >= 50) {
      statusColorClass = "bg-amber-500";
      textColorClass = "text-amber-500";
    }

    return (
      <Card ref={ref} className={cn("w-full overflow-hidden", className)} {...props}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex justify-between items-center">
            Credit Khata Balance
            {dueDate && <span className="text-xs font-normal">Due: {dueDate}</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-4">
          <div className="flex items-end gap-2 mb-4">
            <span className={cn("text-3xl font-bold tracking-tight", textColorClass)}>
              {formatPaise(balance)}
            </span>
          </div>

          {creditLimit > 0 && (
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Limit Used: {Math.round(utilization)}%</span>
                <span>Limit: {formatPaise(creditLimit)}</span>
              </div>
              <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
                <div 
                  className={cn("h-full transition-all duration-500", statusColorClass)} 
                  style={{ width: `${utilization}%` }}
                  role="progressbar"
                  aria-valuenow={utilization}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
            </div>
          )}
        </CardContent>
        {onPayNow && (
          <CardFooter className="pt-0">
            <Button onClick={onPayNow} className="w-full" variant={utilization >= 80 ? "destructive" : "primary"}>
              Pay Now
            </Button>
          </CardFooter>
        )}
      </Card>
    );
  }
);

CreditKhataCard.displayName = 'CreditKhataCard';
